/**
 * CSV modal — run download (client or server).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { GenericStatsRow } from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab/shared/generic-stats.types';
import type { CsvColumnSpec } from '../../buildCsvColumns';
import { buildServerCsvExportOptions } from '../../buildServerCsvExportOptions';
import { buildClientCsvPayload, downloadClientCsvFile } from '../../clientCsvString';
import {
  resolveClientCsvExportRows,
  resolveServerCsvSortOverride,
} from '../../csvExportModalSort';
import type { ServerCsvExportSortConfig } from '../../server-sort';
import type { UseCsvExportModalStateParams } from '../useCsvExportModalState.types';

export interface RunCsvExportModalDownloadParams {
  exportColumnKeys: string[];
  serverCsvExport: UseCsvExportModalStateParams['serverCsvExport'];
  serverCsvSortConfig: ServerCsvExportSortConfig | undefined;
  exportSortField: string;
  exportSortDir: 'asc' | 'desc';
  excludeCrossSourceNotedRows: boolean;
  exportDownloadBaseName: string;
  setServerExporting: (v: boolean) => void;
  onOpenChange: (open: boolean) => void;
  tab: string;
  columns: CsvColumnSpec[];
  rows: GenericStatsRow[];
  csvExportWorkDate: string;
}

export async function runCsvExportModalDownload(
  p: RunCsvExportModalDownloadParams,
): Promise<void> {
  const ordered = p.exportColumnKeys;
  if (p.serverCsvExport) {
    p.setServerExporting(true);
    try {
      const sortOverride = resolveServerCsvSortOverride(
        p.serverCsvSortConfig,
        p.exportSortField,
        p.exportSortDir,
      );
      await p.serverCsvExport(
        ordered,
        sortOverride,
        buildServerCsvExportOptions({
          excludeCrossSourceNotedRows: p.excludeCrossSourceNotedRows,
          exportDownloadBaseName: p.exportDownloadBaseName,
        }),
      );
      p.onOpenChange(false);
    } finally {
      p.setServerExporting(false);
    }
    return;
  }
  const rowsForFile = resolveClientCsvExportRows(
    p.rows,
    p.serverCsvSortConfig,
    p.exportSortField,
    p.exportSortDir,
    p.csvExportWorkDate,
  );
  const { csv, filename } = buildClientCsvPayload(
    p.tab,
    p.columns,
    rowsForFile,
    ordered,
    { downloadBaseName: p.exportDownloadBaseName.trim() || undefined },
  );
  downloadClientCsvFile(csv, filename);
  p.onOpenChange(false);
}
